/**
 * Defaults cliente para site-settings — espejo de api/site-settings DEFAULT_SETTINGS
 * usando VITE_BRAND_* (sin fetch). Worker KV, /api/site-settings y dev local
 * comparten este fallback cuando faltan campos.
 */

import type { SiteSettings } from "@/hooks/useSiteSettings";
import {
  BRAND_AGE,
  BRAND_BANNER_IMAGE_URL,
  BRAND_CLOUDINARY_FOLDER,
  BRAND_PERSON_NAME,
} from "@/lib/brandConfig";
import {
  BRAND_SEO_HOME_DESCRIPTION_ES,
  BRAND_SEO_HOME_TITLE_ES,
} from "@/lib/brandSeoCopy";

export const DEFAULT_SOBRE_MI_TEXT =
  `${BRAND_PERSON_NAME} es modelo y creadora de contenido argentina con más de cinco años de trayectoria en producción propia, proyectos independientes y construcción de audiencia en redes. Una identidad estética consolidada y una comunidad comprometida la posicionan como una figura de referencia para marcas, campañas comerciales y producciones audiovisuales.`;

const DEFAULT_BANNER_URL =
  BRAND_BANNER_IMAGE_URL?.trim() ||
  (BRAND_CLOUDINARY_FOLDER
    ? `https://res.cloudinary.com/nm-creators/image/upload/v1776275522/${BRAND_CLOUDINARY_FOLDER.replace("/portfolio", "")}-header_szqaxs.png`
    : undefined);

/**
 * Galería del home por defecto — fallback cuando la DB no tiene gallery_image_urls.
 * ⚠️ SYNC — debe ser IDÉNTICA en los 3 runtimes:
 *   • cloudflare-worker-delfuocampo/worker.js  → DEFAULT_GALLERY_IMAGE_URLS
 *   • api/site-settings.ts                     → DEFAULT_HOME_GALLERY_IMAGE_URLS
 *   • src/lib/siteSettingsDefaults.ts (este archivo)
 * Fuente única real = la DB (admin /site-settings → Importar + Guardar). Esto es
 * solo la red de seguridad para DB vacía; en cuanto haya galería curada, gana.
 */
export const DEFAULT_HOME_GALLERY_IMAGE_URLS: string[] = [
  "https://res.cloudinary.com/nm-creators/image/upload/v1776275522/delfuocampo-header_szqaxs.png",
  "https://res.cloudinary.com/nm-creators/image/upload/v1776280287/P1140038_bd6t5x.jpg",
  "https://res.cloudinary.com/nm-creators/image/upload/v1776280288/P1140071_bcflbl.jpg",
  "https://res.cloudinary.com/nm-creators/image/upload/v1776280291/P1140062_bcmay7.jpg",
  "https://res.cloudinary.com/nm-creators/image/upload/v1776280294/P1140060_kcng5p.jpg",
  "https://res.cloudinary.com/nm-creators/image/upload/v1776280297/P1140064_kuvr5p.jpg",
];

const DEFAULT_TITLE = BRAND_PERSON_NAME ? BRAND_SEO_HOME_TITLE_ES : "";

const DEFAULT_DESCRIPTION = BRAND_SEO_HOME_DESCRIPTION_ES;

/** Estado inicial cuando no hay Worker KV ni respuesta API aún. */
export const SITE_SETTINGS_CLIENT_DEFAULTS: SiteSettings = {
  headerUrl: "/",
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  sobreMi: DEFAULT_SOBRE_MI_TEXT,
  showSobreMi: true,
  bannerImageUrl: DEFAULT_BANNER_URL,
  galleryImageUrls: DEFAULT_HOME_GALLERY_IMAGE_URLS,
  showGallerySection: true,
  showTallySection: true,
  alternativeGalleryEnabled: false,
  brandAge: BRAND_AGE?.trim() || undefined,
};

/** Rellena huecos sin pisar valores admin explícitos (incl. strings vacíos intencionales en campos opcionales). */
export function withSiteSettingsDefaults(
  partial: Partial<SiteSettings>,
): SiteSettings {
  const title = partial.title?.trim();
  const description = partial.description?.trim();
  const headerUrl = partial.headerUrl?.trim();
  const banner =
    typeof partial.bannerImageUrl === "string" && partial.bannerImageUrl.trim()
      ? partial.bannerImageUrl.trim()
      : undefined;
  // Galería: NO se rellena con default acá. Se obedece lo que mandó el data layer
  // (Worker/api): `[]` cuando la fila existe con galería vacía, las 6 fotos cuando
  // no hay fila. El default `SITE_SETTINGS_CLIENT_DEFAULTS.galleryImageUrls` solo
  // aplica vía el spread cuando `partial` no trae la clave (estado inicial sin fetch).

  return {
    ...SITE_SETTINGS_CLIENT_DEFAULTS,
    ...partial,
    headerUrl: headerUrl || SITE_SETTINGS_CLIENT_DEFAULTS.headerUrl,
    title: title || SITE_SETTINGS_CLIENT_DEFAULTS.title,
    description: description || SITE_SETTINGS_CLIENT_DEFAULTS.description,
    sobreMi: partial.sobreMi?.trim() || SITE_SETTINGS_CLIENT_DEFAULTS.sobreMi,
    bannerImageUrl: banner ?? SITE_SETTINGS_CLIENT_DEFAULTS.bannerImageUrl,
    brandAge: partial.brandAge?.trim() || SITE_SETTINGS_CLIENT_DEFAULTS.brandAge,
    showSobreMi:
      typeof partial.showSobreMi === "boolean"
        ? partial.showSobreMi
        : SITE_SETTINGS_CLIENT_DEFAULTS.showSobreMi,
    showGallerySection:
      typeof partial.showGallerySection === "boolean"
        ? partial.showGallerySection
        : SITE_SETTINGS_CLIENT_DEFAULTS.showGallerySection,
    showTallySection:
      typeof partial.showTallySection === "boolean"
        ? partial.showTallySection
        : SITE_SETTINGS_CLIENT_DEFAULTS.showTallySection,
    alternativeGalleryEnabled:
      typeof partial.alternativeGalleryEnabled === "boolean"
        ? partial.alternativeGalleryEnabled
        : SITE_SETTINGS_CLIENT_DEFAULTS.alternativeGalleryEnabled,
  };
}